// Turns whatever a failed request (or a thrown value) carries into a readable message.
// Handles: string, { message }, { title, detail }, { errors: { Field: [messages] } },
// RTK Query errors ({ status, data }), Error instances and arrays of any of these.
// It never returns "[object Object]" or raw JSON.

const MAX_DEPTH = 5;

const clean = (text: string): string | null => {
  const trimmed = text.trim();

  if (!trimmed || trimmed === '[object Object]') {
    return null;
  }

  if (/^[[{]/.test(trimmed)) {
    try {
      return collect(JSON.parse(trimmed), 1)[0] ?? null;
    } catch {
      return null;
    }
  }

  return trimmed;
};

const collect = (value: unknown, depth: number): string[] => {
  if (value === null || value === undefined || depth > MAX_DEPTH) {
    return [];
  }

  if (typeof value === 'string') {
    const text = clean(value);
    return text ? [text] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => collect(item, depth + 1));
  }

  if (value instanceof Error) {
    return collect(value.message, depth + 1);
  }

  if (typeof value === 'object') {
    const { errors, detail, message, title, data } = value as Record<string, unknown>;

    // Field errors first: "title" is usually a generic "One or more validation errors occurred."
    const fieldMessages = errors && typeof errors === 'object' && !Array.isArray(errors)
      ? Object.values(errors).flatMap((item) => collect(item, depth + 1))
      : collect(errors, depth + 1);

    const candidates = [fieldMessages, collect(detail, depth + 1), collect(message, depth + 1), collect(title, depth + 1), collect(data, depth + 1)];
    return candidates.find((list) => list.length > 0) ?? [];
  }

  return [];
};

export const getErrorMessages = (error: unknown): string[] => Array.from(new Set(collect(error, 0)));

export const getErrorMessage = (error: unknown, fallback = ''): string => getErrorMessages(error)[0] ?? fallback;

/** { errors: { Iban: ['...'] } } (also inside RTK `data`) -> { iban: '...' } with the first message per field. */
export const getFieldErrors = (error: unknown): Record<string, string> => {
  const source: any = (error as any)?.data ?? error;
  const errors = source?.errors;

  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return {};
  }

  return Object.entries(errors).reduce<Record<string, string>>((acc, [field, messages]) => {
    const message = getErrorMessage(messages);
    return message ? { ...acc, [field.charAt(0).toLowerCase() + field.slice(1)]: message } : acc;
  }, {});
};

/** Error that also carries messages for individual form fields (useForm shows them under the fields). */
export class FormSubmitError extends Error {
  fieldErrors: Record<string, string>;

  constructor (message: string, fieldErrors: Record<string, string> = {}) {
    super(message);
    this.name = 'FormSubmitError';
    this.fieldErrors = fieldErrors;
  }
}
